/**
 * configParser — the ONE place that knows the cloud device-config shape (INI and modern JSON),
 * for BOTH engine families, at the camera, device, AND connector level.
 *
 * Cloud setup edits the cloudapi DB `JSDeviceConfig.sConfig` (an INI or JSON blob), NOT the device directly.
 * Two device families store DIFFERENT section/key names in that blob:
 *
 *   dragon (MODERN) → [camN]     + [global]/[server]/[llm]/[mpengine]
 *   legacy (CLOUD)  → [cameraN]  + [global]/[libaiengine]
 *
 * "BOIL THE LAKE" (David 2026-07-22): surface EVERY field each engine's parser can act on — per
 * camera and per device — seeded to the engine's default and WRITTEN even when the stored blob
 * omits it, so an admin can set any option the device supports. This deliberately REVERSES the
 * 2026-07-20 conservative "write only keys already present" rule, per family, using each engine's
 * OWN key list.
 *
 * Field SOURCES (authoritative — read straight from the engine parsers, not from docs):
 *   dragon → dragon/config.c  handler()          + config.h defaults (find_or_add_cam / config_parse)
 *   legacy → isite/engine/src/config.c  ini_handler() + config.h defaults (LOCALTIME / USE_B2)
 * nvr/mpengine is the ON-BOX NVR engine — it is NOT a cloud engine, so it is deliberately NOT a
 * source here.
 *
 * Two safety invariants carried over from the prior design:
 *   1. PRESERVE UNKNOWN KEYS. The live edge build can ship keys newer than the checked-out parser
 *      (verified on web1: dragon [global]thumb_width/thumb_delay, [videostore], [llm]scene_prompt).
 *      Every key we don't manage passes through a write untouched; every other section is preserved.
 *   2. STAY WITHIN THE FAMILY. We only ever write keys in the detected family's own parser key list,
 *      so we never inject a key the target engine would reject as unknown.
 *
 * The camera's human-readable name lives in the config section's own `name` key — a FREE LABEL that
 * is independent of the section HEADER (`[camN]`), which is the device-internal stream id used for URL
 * composition (confirmed with Brad 2026-07-27). So `name` is EDITABLE here and the CONFIG is its
 * source of truth. JSCamera.sName is kept only as a MIRROR — written alongside via the /ve/camera
 * `name` param — so peripheral surfaces that read sName (live/playback overlay, saved-view labels, the
 * user-access camera grid) stay correct without re-parsing the blob. (Was: sName authoritative, config
 * `name` read-only — flipped once Brad confirmed the header, not `name`, is the stream id.)
 */

/** The two known cloud device families, distinguished by their config section naming. */
export const FAMILIES = Object.freeze({
  dragon: Object.freeze({ key: 'dragon', sectionPrefix: 'cam', urlKeys: ['rec_url', 'live_url'], rich: true }),
  legacy: Object.freeze({ key: 'legacy', sectionPrefix: 'camera', urlKeys: ['url1', 'url2'], rich: false }),
});

// ---------------------------------------------------------------------------
// Modern JSON format support — `{ format: 1, files: { dragon, connector } }`
//
// Legacy devices store a flat INI blob; modern dragon devices store a JSON blob
// with two namespaces: `files.dragon` (engine sections) and `files.connector`
// (recorder/POS/cloud — what Java dview calls the POS tab). All existing
// read/write functions reach sections through engineSections() so they work
// transparently on either format. connectorSections() exposes the POS namespace.
// ---------------------------------------------------------------------------

/** Is this the modern JSON config format (format:1 with files namespace)? */
export const isModernFormat = (config) =>
  config != null && config.format === 1 && config.files != null;

/**
 * The flat section namespace for the engine (dragon/camera config).
 * Modern JSON: config.files.dragon. INI: the config object itself.
 */
export const engineSections = (config) =>
  isModernFormat(config) ? (config.files.dragon || {}) : (config || {});

/**
 * The flat section namespace for the connector (POS/cloud/rpc config).
 * Modern JSON only — returns null for INI configs (legacy has no connector).
 */
export const connectorSections = (config) =>
  isModernFormat(config) ? (config.files.connector || {}) : null;

/**
 * Re-wrap modified engine sections back into the full config format for writeback.
 * Modern JSON: reconstructs the wrapper preserving the connector side.
 * INI: returns the modified engine sections directly (they ARE the config).
 */
const rewrap = (config, modifiedEngine) =>
  isModernFormat(config)
    ? { format: config.format, files: { ...config.files, dragon: modifiedEngine } }
    : modifiedEngine;

/** The 8×8 motion-detection grid bitmask key — shared by both families. */
export const MOTION_MASK_KEY = 'motion_grid_mask';

/** Config values are quoted strings ('1'/'0'); coerce to/from bool for the UI toggles. */
export const boolFromCfg = (v) => v === '1' || v === 1 || v === true || v === 'true' || v === 'on';
export const cfgFromBool = (b) => (b ? '1' : '0');

// ---------------------------------------------------------------------------
// Field schemas — the authoritative per-family surface, straight from each parser.
//
// Each descriptor: { form, key, kind, access, def }
//   form   — the camelCase form-view field name (what the React tabs read/patch)
//   key    — the INI key inside the section
//   kind   — 'bool' | 'num' | 'text' | 'mask' | 'roi'  (drives read coercion + write serialization)
//   access — the shared gating mechanism (setupCommon.resolveDisp): 'edit' (master edits, local
//            read-only) | 'readonly' (never written, read-only for all) | 'hidden' | or a full
//            { master, local } pair. Replaced the old 'w'/'adv'/'ro' flags (David 2026-08-21).
//   def    — the engine default, as it appears in the INI (used to SEED an absent field)
// ---------------------------------------------------------------------------

// dragon camera keys — dragon/config.c handler() `cam` branch; defaults from find_or_add_cam.
const DRAGON_CAM = [
  { form: 'enabled',        key: 'enabled',          kind: 'bool', access: 'edit',   def: '0' },
  { form: 'configName',     key: 'name',             kind: 'text', access: 'edit',   def: '' },
  { form: 'recUrl',         key: 'rec_url',          kind: 'text', access: 'edit',   def: '' },
  { form: 'liveUrl',        key: 'live_url',         kind: 'text', access: 'edit',   def: '' },
  { form: 'motionMask',     key: 'motion_grid_mask', kind: 'mask', access: 'edit',   def: '0xffffffffffffffff' },
  { form: 'motionSens',     key: 'motion_sens',      kind: 'num',  access: 'edit',   def: '0.25' },
  { form: 'motionPost',     key: 'motion_post',      kind: 'num',  access: 'edit',   def: '10' },
  { form: 'objectOnly',     key: 'object_only',      kind: 'bool', access: 'edit',   def: '0' },
  { form: 'useOnnx',        key: 'use_onnx',         kind: 'bool', access: 'edit',   def: '0' },
  { form: 'mpe',            key: 'mpe',              kind: 'bool', access: 'edit', def: '0' },
  { form: 'onvifUseTracker', key: 'onvif_use_tracker', kind: 'bool', access: 'edit', def: '0' },
  { form: 'usePose',        key: 'use_pose',         kind: 'bool', access: 'edit',   def: '0' },
  { form: 'useLpr',         key: 'use_lpr',          kind: 'bool', access: 'edit',   def: '0' },
  { form: 'lprRoi',         key: 'lpr_roi',          kind: 'roi',  access: 'edit',   def: '0,0,1,1' },
  { form: 'lprMotionSens',  key: 'lpr_motion_sens',  kind: 'num',  access: 'edit',   def: '0.25' },
  { form: 'lprMotionPost',  key: 'lpr_motion_post',  kind: 'num',  access: 'edit',   def: '10' },
  { form: 'ptz',            key: 'ptz',              kind: 'text', access: 'edit',   def: '' },
];

// legacy camera keys — isite/engine/src/config.c ini_handler() `camera` branch.
// isite memset-zeros the struct (no per-cam default init), so the raw engine default is 0 for the
// numeric keys. Exception: motion_grid_mask seeds ALL-ON — every real legacy config carries
// 0xFFFFFFFFFFFFFFFF, and the memset-0 (all cells disabled) is a degenerate uninitialized value that
// no fielded device uses. still_interval is stored in MINUTES (the parser multiplies by 60).
const LEGACY_CAM = [
  { form: 'enabled',        key: 'enabled',          kind: 'bool', access: 'edit',   def: '0' },
  { form: 'configName',     key: 'name',             kind: 'text', access: 'edit',   def: '' },
  { form: 'recUrl',         key: 'url1',             kind: 'text', access: 'edit',   def: '' },
  { form: 'liveUrl',        key: 'url2',             kind: 'text', access: 'edit',   def: '' },
  { form: 'jpgUrl',         key: 'jpg_url',          kind: 'text', access: 'readonly',  def: '' },
  { form: 'motionMask',     key: 'motion_grid_mask', kind: 'mask', access: 'edit',   def: '0xffffffffffffffff' },
  { form: 'motionSens',     key: 'motion_sens',      kind: 'num',  access: 'edit',   def: '0' },
  { form: 'motionPost',     key: 'motion_post',      kind: 'num',  access: 'edit',   def: '0' },
  { form: 'stillInterval',  key: 'still_interval',   kind: 'num',  access: 'edit',   def: '0' },
  { form: 'ptz',            key: 'ptz',              kind: 'text', access: 'edit',   def: '' },
];

// dragon device keys — [global]/[server]/[llm]/[mpengine]. cert_file/key_file are TLS material and
// stay skipped (never surfaced, always preserved). require_auth/use_backend are "foot-guns" (turning
// either off breaks something big) — a Dividia master may still edit them (access 'edit'). Cloud
// backend (use_backend) LEADS the Config tab as the master cloud-connection switch; the Media server
// foot-gun (require_auth) sorts last (group order lives in CloudDeviceSetup's DEVICE_GROUPS).
const DRAGON_DEV = [
  { form: 'llmEnabled',   section: 'llm',      key: 'enabled',       kind: 'bool', access: 'edit',   def: '0',  label: 'Scene descriptions (LLM)' },
  { form: 'mpeEnabled',   section: 'mpengine', key: 'enabled',       kind: 'bool', access: 'edit',   def: '0',  label: 'mpengine event bridge' },
  { form: 'requireAuth',  section: 'server',   key: 'require_auth',  kind: 'bool', access: 'edit', def: '1',  label: 'Require auth' },
  { form: 'useBackend',   section: 'global',   key: 'use_backend',   kind: 'bool', access: 'edit', def: '0',  label: 'Connect to cloud backend' },
  { form: 'llmBackend',   section: 'llm',      key: 'backend',       kind: 'text', access: 'readonly',  def: '',   label: 'LLM backend' },
  { form: 'llmHost',      section: 'llm',      key: 'host',          kind: 'text', access: 'readonly',  def: '',   label: 'LLM host' },
  { form: 'llmPort',      section: 'llm',      key: 'port',          kind: 'num',  access: 'readonly',  def: '',   label: 'LLM port' },
  { form: 'llmModel',     section: 'llm',      key: 'model',         kind: 'text', access: 'readonly',  def: '',   label: 'LLM model' },
  { form: 'mpeHost',      section: 'mpengine', key: 'host',          kind: 'text', access: 'readonly',  def: '',   label: 'mpengine host' },
  { form: 'mpePort',      section: 'mpengine', key: 'port',          kind: 'num',  access: 'readonly',  def: '',   label: 'mpengine port' },
  { form: 'httpPort',     section: 'server',   key: 'http_port',     kind: 'num',  access: 'readonly',  def: '',   label: 'HTTP port' },
  { form: 'httpsPort',    section: 'server',   key: 'https_port',    kind: 'num',  access: 'readonly',  def: '',   label: 'HTTPS port' },
  { form: 'httpsEnabled', section: 'server',   key: 'https_enabled', kind: 'bool', access: 'edit',  def: '0',  label: 'HTTPS enabled' },
  { form: 'certFile',     section: 'server',   key: 'cert_file',     kind: 'text', access: 'readonly',  def: '',   label: 'Certificate file' },
  { form: 'keyFile',      section: 'server',   key: 'key_file',      kind: 'text', access: 'readonly',  def: '',   label: 'Key file' },
  // global fields promoted from "other" (verified on web1 00E00B83023F 2026-08-18)
  // conf_rev: the engine [global] config revision. Server-owned (cloudapi modifyDeviceConfig does the
  // atomic compare-and-increment), so shown READ-ONLY. Declared here so it surfaces under Config → Global
  // as "Config revision" instead of leaking into Misc as an unmanaged "global · conf_rev" (#86bbfuajj, David).
  { form: 'confRev',      section: 'global',   key: 'conf_rev',      kind: 'num',  access: 'readonly',  def: '0',   label: 'Config revision' },
  { form: 'thumbWidth',   section: 'global',   key: 'thumb_width',   kind: 'num',  access: 'readonly',  def: '160', label: 'Thumbnail width' },
  { form: 'thumbDelay',   section: 'global',   key: 'thumb_delay',   kind: 'num',  access: 'readonly',  def: '2',   label: 'Thumbnail delay' },
  { form: 'poseEnabled',  section: 'global',   key: 'pose_enabled',  kind: 'bool', access: 'edit',  def: '0',   label: 'Pose detection' },
  { form: 'recordPro2',   section: 'global',   key: 'record_pro2',   kind: 'bool', access: 'edit',  def: '0',   label: 'Record profile 2' },  // known [global] toggle — tracked here so it never falls into Misc
  // Device OS / recording timezone (dragon [global].timezone). Declared here so it renders under
  // Config → Global (editable) instead of leaking into Misc. Gating is the single source (setupAccess
  // 'global.timezone' = master+local edit). Free-text for now (IANA name); a picker is a Sprint-34 follow-up.
  { form: 'timezone',     section: 'global',   key: 'timezone',      kind: 'enum', options: 'tz', access: 'edit', def: '', label: 'Timezone', hint: 'Sets this device recording + overlay timezone.' },  // 'tz' → ConfigGroup fills the shared IANA dropdown (same as the Site picker)
  // videostore paths (read-only device facts)
  { form: 'vs1',          section: 'videostore', key: 'vs1',          kind: 'text', access: 'readonly',  def: '',    label: 'Path 1' },
  { form: 'vs2',          section: 'videostore', key: 'vs2',          kind: 'text', access: 'readonly',  def: '',    label: 'Path 2' },
];

// legacy device keys — [global]/[libaiengine]. obj_* are on-device detector-model paths (read-only).
// localtime/use_b2 are primary Recording settings (access 'w') — not foot-guns — so they sit as
// top-level toggles in the Recording group (the old device Config carried no lonely "Advanced").
const LEGACY_DEV = [
  { form: 'localtime',  section: 'global',      key: 'localtime',   kind: 'bool', access: 'edit',   def: '0', label: 'Use local time', hint: 'Record timestamps in local time instead of UTC.' },
  { form: 'useB2',      section: 'global',      key: 'use_b2',      kind: 'bool', access: 'edit',   def: '0', label: 'Backblaze B2 upload', hint: 'Upload recordings from this device to Backblaze B2 cloud storage.' },
  { form: 'objNames',   section: 'libaiengine', key: 'obj_names',   kind: 'text', access: 'readonly',  def: '', label: 'Detector names' },
  { form: 'objConfig',  section: 'libaiengine', key: 'obj_config',  kind: 'text', access: 'readonly',  def: '', label: 'Detector config' },
  { form: 'objWeights', section: 'libaiengine', key: 'obj_weights', kind: 'text', access: 'readonly',  def: '', label: 'Detector weights' },
];

// ---------------------------------------------------------------------------
// Connector field schemas — the recorder/POS/cloud config namespace (modern JSON only).
// In Java dview this is the POS tab. Editable fields (access 'edit') are editable by a Dividia master;
// read-only facts (access 'readonly') stay read-only for everyone (resolved via resolveDisp).
// saveDevice POSTs the payload to /ve/device (David 2026-08-21); the backend persists JSDevice +
// JSCamera + JSDeviceConfig (config persistence enabled).
//
// Each descriptor: { form, section, key, kind, access, def, label }
//   section = the key inside files.connector (e.g. 'global', 'rpc', 'dragon', 'cloud')
// ---------------------------------------------------------------------------

// access defaults to 'edit' (master edits / local read-only) for connector config so a Dividia master
// can edit these values incl. text/num fields; lock or hide specific ones later via per-field access
// data (David 2026-08-24). Cloud endpoint facts (api/ws/token) stay 'readonly' — backend-assigned.
const CONNECTOR_GLOBAL = [
  { form: 'connConfRev',        section: 'global', key: 'conf_rev',          kind: 'num',  access: 'readonly', def: '0',   label: 'Config revision' },  // server-owned (cloudapi compare-and-increments); shown read-only
  { form: 'connSerial',         section: 'global', key: 'serial',            kind: 'num',  access: 'edit', def: '',    label: 'Serial' },
  { form: 'connJpegQuality',    section: 'global', key: 'jpeg_quality',      kind: 'num',  access: 'edit', def: '70',  label: 'JPEG quality' },
  { form: 'connTmpdir',         section: 'global', key: 'tmpdir',            kind: 'text', access: 'edit', def: '',    label: 'Temp directory' },
  { form: 'connTicketsdir',     section: 'global', key: 'ticketsdir',        kind: 'text', access: 'edit', def: '',    label: 'Tickets directory' },
  { form: 'connDbfile',         section: 'global', key: 'dbfile',            kind: 'text', access: 'edit', def: '',    label: 'Database file' },
  { form: 'connPairTareLoadout', section: 'global', key: 'pair_tare_loadout', kind: 'bool', access: 'edit', def: '0', label: 'Pair tare/loadout' },
];

const CONNECTOR_RPC = [
  { form: 'connRpcBind', section: 'rpc', key: 'bind', kind: 'text', access: 'edit', def: '127.0.0.1', label: 'RPC bind address' },
  { form: 'connRpcPort', section: 'rpc', key: 'port', kind: 'num',  access: 'edit', def: '43204',     label: 'RPC port' },
];

const CONNECTOR_DRAGON = [
  { form: 'connDragonHost',     section: 'dragon', key: 'host',             kind: 'text', access: 'edit', def: '127.0.0.1', label: 'Engine host' },
  { form: 'connDragonPort',     section: 'dragon', key: 'port',             kind: 'num',  access: 'edit', def: '80',        label: 'Engine port' },
  { form: 'connDragonUser',     section: 'dragon', key: 'user',             kind: 'text', access: 'edit', def: '',          label: 'Engine user' },
  { form: 'connDragonSnap',     section: 'dragon', key: 'snapshot_profile', kind: 'num',  access: 'edit', def: '1',         label: 'Snapshot profile' },
  { form: 'connDragonMeta',     section: 'dragon', key: 'meta_profile',     kind: 'num',  access: 'edit', def: '1',         label: 'Meta profile' },
  { form: 'connDragonConf',     section: 'dragon', key: 'conf',             kind: 'text', access: 'edit', def: '',          label: 'Config path' },
];

const CONNECTOR_CLOUD = [
  { form: 'connCloudEnabled', section: 'cloud', key: 'enabled',   kind: 'bool', access: 'edit', def: '0',  label: 'Enable Cloud' },
  { form: 'connCloudApi',     section: 'cloud', key: 'api',       kind: 'text', access: 'readonly', def: '',   label: 'API endpoint' },
  { form: 'connCloudWs',      section: 'cloud', key: 'ws',        kind: 'text', access: 'readonly', def: '',   label: 'WebSocket endpoint' },
  { form: 'connCloudToken',   section: 'cloud', key: 'api_token', kind: 'text', access: { master: 'readonly', local: 'hidden' }, def: '', label: 'API token' },
];

const ALL_CONNECTOR_FIELDS = [...CONNECTOR_GLOBAL, ...CONNECTOR_RPC, ...CONNECTOR_DRAGON, ...CONNECTOR_CLOUD];

/** The camera field schema for a family. */
export const cameraFields = (family) => (family && family.key === 'legacy' ? LEGACY_CAM : DRAGON_CAM);
/** The device field schema for a family. */
export const deviceFields = (family) => (family && family.key === 'legacy' ? LEGACY_DEV : DRAGON_DEV);
/** The connector field schema (modern JSON only; null for legacy/INI). */
export const connectorFields = () => ALL_CONNECTOR_FIELDS;

// ---------------------------------------------------------------------------
// PTZ driver protocols — the `ptz=` value that ENABLES pan/tilt/zoom on a cloud edge device.
// Presence of a recognized driver name turns PTZ on; an empty string ('') disables it — there is NO
// separate enable flag (dragon main.c:143 `if (cc->ptz && cc->ptz[0])`; legacy isite/engine
// config.c:63 stores it only `&& strlen(value)`).
//
// Sourced from the two CLOUD EDGE engines' OWN driver tables — deliberately NOT the local-NVR `ptzd`
// daemon (nvr/ptzd is a different product with a wider vendor list). Both cloud engines accept the
// same 5, because dragon descends from isite/engine:
//   dragon → dragon/ptz.c        ptz_init()  (axis / onvif / ens / acti / zavio)
//   legacy → isite/engine/ptz.c  ptz_init()  (axis / onvif / ens / acti / zavio)
// value = the exact lowercase string the engine matches (case-insensitively); label = display name.
const CLOUD_PTZ_DRIVERS = Object.freeze([
  { value: 'axis',  label: 'Axis' },
  { value: 'onvif', label: 'ONVIF' },
  { value: 'ens',   label: 'ENS' },
  { value: 'acti',  label: 'ACTi' },
  { value: 'zavio', label: 'Zavio' },
]);

/**
 * The PTZ driver protocols selectable for a cloud device family. Both cloud edge engines (dragon and
 * legacy isite/engine) currently accept the same set; the `family` arg is the seam if they ever
 * diverge in a live edge build. The UI adds a "None" ('') choice to disable PTZ, and preserves any
 * already-stored driver not in this list (so a newer edge build's driver is never clobbered on save).
 * @param {Object} [family] - detected family (unused today; both families share the list)
 * @returns {ReadonlyArray<{value:string,label:string}>}
 */
export const ptzDrivers = (/* family */) => CLOUD_PTZ_DRIVERS;

// The union of every camera form-key across families — so `present`/`has` maps carry an explicit
// `false` for a field the *current* family doesn't support (e.g. dragon has no stillInterval).
const ALL_CAM_FORM_KEYS = Array.from(new Set([...DRAGON_CAM, ...LEGACY_CAM].map((d) => d.form)));

// Coerce an INI value (or seeded default) into the form-view type.
const coerce = (kind, v) => (kind === 'bool' ? boolFromCfg(v) : (v == null ? '' : String(v)));
// Serialize a form value back to stored-config form. Modern JSON (dragon) stores NATIVE types:
// numeric-kind ('num') and boolean-kind fields serialize as JS NUMBERS so the DB JSON blob is numeric
// (2, 1) — the device requires real numbers and BREAKS on the string form ("2", "1"). Legacy INI
// stores everything as strings (bool -> '1'/'0', else String()). `numeric` = isModernFormat(config),
// computed once per write by writeCamera/writeDevice. (#86bbm58f7 numeric-type bug, root-caused by Brad)
const serialize = (kind, v, numeric) => {
  if (kind === 'bool') return numeric ? (boolFromCfg(v) ? 1 : 0) : cfgFromBool(v);
  if (numeric && kind === 'num') {
    const n = Number(v);
    return v != null && v !== '' && Number.isFinite(n) ? n : (v == null ? '' : String(v));
  }
  return v == null ? '' : String(v);
};

// TLS material we never surface and always preserve (dragon [server]).
const DEVICE_SKIP = { server: { cert_file: true, key_file: true } };

/**
 * Which family a device config is, by its section naming (authoritative). Falls back to the
 * dragon/legacy top-level markers, then defaults to dragon (the modern shape) for an empty config.
 * Works on both INI (flat) and modern JSON (format:1) configs — inspects the engine sections.
 * @param {Object} config - parsed config (INI flat or modern JSON wrapper)
 * @returns {{key:string, sectionPrefix:string, urlKeys:string[], rich:boolean}}
 */
export const detectFamily = (config) => {
  const sections = engineSections(config);
  const keys = Object.keys(sections);
  if (keys.some((k) => /^camera\d+$/i.test(k))) return FAMILIES.legacy;
  if (keys.some((k) => /^cam\d+$/i.test(k))) return FAMILIES.dragon;
  if (sections.libaiengine) return FAMILIES.legacy;
  if (sections.llm || sections.videostore) return FAMILIES.dragon;
  return FAMILIES.dragon;
};

/**
 * Classify a device config for display. Returns { family, pill, pillClass } where:
 *   family  — FAMILIES.dragon | FAMILIES.legacy (for config consumers; defaults dragon for unknown)
 *   pill    — 'DRAGON' | 'LEGACY' | 'NO CONFIG' (the user-facing label)
 *   pillClass — CSS class suffix: 'modern' | 'legacy' | 'unknown'
 *
 * Modern JSON (format:1) is always dragon. An empty/null config is "NO CONFIG".
 * A non-empty config with no recognizable family markers is "NO CONFIG" — it is NOT assumed dragon.
 */
export const classifyDevice = (config) => {
  if (isModernFormat(config)) return { family: FAMILIES.dragon, pill: 'DRAGON', pillClass: 'modern' };
  const keys = config && typeof config === 'object' ? Object.keys(config) : [];
  if (!keys.length) return { family: FAMILIES.dragon, pill: 'NO CONFIG', pillClass: 'unknown' };
  const sections = engineSections(config);
  const sKeys = Object.keys(sections);
  if (sKeys.some((k) => /^camera\d+$/i.test(k))) return { family: FAMILIES.legacy, pill: 'LEGACY', pillClass: 'legacy' };
  if (sections.libaiengine) return { family: FAMILIES.legacy, pill: 'LEGACY', pillClass: 'legacy' };
  if (sKeys.some((k) => /^cam\d+$/i.test(k))) return { family: FAMILIES.dragon, pill: 'DRAGON', pillClass: 'modern' };
  if (sections.llm || sections.videostore) return { family: FAMILIES.dragon, pill: 'DRAGON', pillClass: 'modern' };
  return { family: FAMILIES.dragon, pill: 'NO CONFIG', pillClass: 'unknown' };
};

/** The config section key for a camera number, e.g. dragon cam 3 → 'cam3', legacy → 'camera3'. */
export const sectionFor = (family, cam) => `${family.sectionPrefix}${cam}`;

/**
 * The camera numbers that HAVE a section in this config, numerically sorted. Cloud is edit-only,
 * so the config's sections define the camera list (no add).
 * @returns {number[]}
 */
export const listConfigCameras = (config, family) => {
  const fam = family || detectFamily(config);
  const sections = engineSections(config);
  const re = new RegExp(`^${fam.sectionPrefix}(\\d+)$`, 'i');
  return Object.keys(sections)
    .map((k) => { const m = k.match(re); return m ? Number(m[1]) : null; })
    .filter((n) => n != null)
    .sort((a, b) => a - b);
};

/** The raw section object for one camera (or {}). */
export const cameraSection = (config, cam, family) => {
  const fam = family || detectFamily(config);
  const sections = engineSections(config);
  return sections[sectionFor(fam, cam)] || {};
};

/**
 * Camera counts DERIVED FROM THE CONFIG — the honest camera tally for a device, replacing the
 * unreliable JSDevice.bCameras (a device-reported/registration value cloudapi never writes on the
 * admin path, so it goes stale on the test bed: e.g. 00E00B83023F reports bCameras=10 while its
 * config carries 14 sections). `authorized` = how many camera sections the config carries
 * (camN / cameraN); `enabled` = how many of those have enabled=1. Read straight from the parsed
 * config so the Device screen and the Camera screen always agree.
 * @param {Object} config - parsed INI object
 * @param {Object} [family] - detected if omitted
 * @returns {{authorized:number, enabled:number}}
 */
export const cameraCounts = (config, family) => {
  const fam = family || detectFamily(config);
  const cams = listConfigCameras(config, fam);
  let enabled = 0;
  for (const cam of cams) {
    if (boolFromCfg(cameraSection(config, cam, fam).enabled)) enabled += 1;
  }
  return { authorized: cams.length, enabled };
};

/**
 * A normalized "form view" of a camera's section, BOIL-THE-LAKE style: every field the family's
 * engine supports is populated — from the blob if present, else seeded from the engine default.
 * `present` says which fields the family supports (drives which controls render — NOT whether the
 * blob carried the key); `has` says which keys the blob actually carried (blob truth, for hints).
 * `raw` is kept so writeCamera can preserve unknown keys.
 * @returns {{cam:number, family:string, rich:boolean, raw:Object, present:Object, has:Object,
 *   configName:string, enabled:boolean, recUrl:string, liveUrl:string, jpgUrl:string,
 *   motionMask:string, motionSens:string, motionPost:string, stillInterval:string,
 *   objectOnly:boolean, useOnnx:boolean, mpe:boolean, onvifUseTracker:boolean, useLpr:boolean,
 *   lprRoi:string, lprMotionSens:string, lprMotionPost:string, ptz:string}}
 */
export const readCamera = (config, cam, family) => {
  const fam = family || detectFamily(config);
  const s = cameraSection(config, cam, fam);
  const fields = cameraFields(fam);
  const present = {};
  for (const k of ALL_CAM_FORM_KEYS) present[k] = false;
  const has = {};
  const view = { cam, family: fam.key, rich: fam.rich, raw: s };
  for (const d of fields) {
    const hasKey = d.key in s;
    view[d.form] = coerce(d.kind, hasKey ? s[d.key] : d.def);
    present[d.form] = true;   // family supports it → render a control (seeded if the blob lacked it)
    has[d.form] = hasKey;     // did the stored blob actually carry it
  }
  view.present = present;
  view.has = has;
  return view;
};

/**
 * Merge a camera form back into its config section. IMMUTABLE (returns a NEW config).
 *   - writes ONLY the writable keys the ORIGINAL section actually carried (present-only — the device
 *     dumps its full config and we mirror exactly what's there; we don't optimistically add keys the
 *     device never sent — David 2026-08-26),
 *   - never writes a key outside the family (so the target engine never sees an unknown key),
 *   - preserves every other key in the section (unknown/newer fields survive) and every other section.
 * @param {Object} config - the FULL parsed device config
 * @param {number} cam
 * @param {Object|null} family
 * @param {Object} form - a readCamera()-shaped object
 * @returns {Object} the new full config
 */
export const writeCamera = (config, cam, family, form) => {
  const fam = family || detectFamily(config);
  const key = sectionFor(fam, cam);
  const engine = engineSections(config);
  const numeric = isModernFormat(config);   // modern JSON stores native number/bool; INI stays strings
  const prev = engine[key] || {};
  const next = { ...prev };
  for (const d of cameraFields(fam)) {
    // NO access check here — same as writeDevice (David 2026-08-27). Editability is decided ONLY by the
    // single source (setupAccess) at render time; write persists any present key whose VALUE CHANGED, so a
    // read-only (or simply untouched) field is a no-op. The schema `access` no longer gates persistence, so
    // camera and device write paths follow the ONE mechanism. (#86bbm6j8w)
    if (form[d.form] === undefined) continue;
    if (!Object.prototype.hasOwnProperty.call(prev, d.key)) continue;   // device didn't send this key — don't add it
    const serialized = serialize(d.kind, form[d.form], numeric);
    if (serialized === prev[d.key]) continue;   // unchanged — don't rewrite (preserves ref; RO fields no-op)
    next[d.key] = serialized;
  }
  return rewrap(config, { ...engine, [key]: next });
};

// ---------------------------------------------------------------------------
// Camera DISCOVERY → config (subtask #86bbm53r2)
//
// Discovery is the ONLY "add a camera" path in cloud setup (existing cameras
// auto-register from the device; writeCamera is edit-only + present-only). A
// discovered camera becomes a recording camera by staging a NEW [camN] section
// into the device config — which then rides the normal "Push to Device" batch
// (single conf_rev bump → conf_sync → the device applies it). This SEEDS rec_url/live_url
// from the discovered camera's RTSP URLs (which already carry embedded credentials from the
// device's ONVIF probe). For an EXISTING camera those same two URLs are master-editable on the
// Cameras screen (access 'edit', local read-only) — writeCamera persists an edit like any field.
// Everything else is left to the device's own per-camera defaults on apply.
// ---------------------------------------------------------------------------

const MAX_CAMERA_NUMBER = 64; // dragon/config.h MAX_CAM
// Mirrors cloudapi deviceConfig.js local-admin camera validation (#86bc63u9g): rtsp(s) stream URLs,
// make/model <= 128 chars (name <= 256), no control characters.
const CAMERA_TEXT_MAX = 128;
export const isStreamUrl = (url) => typeof url === 'string' && url.length <= 2048
  // eslint-disable-next-line no-control-regex
  && /^rtsps?:\/\/[^\s\x00-\x1f\x7f]+$/i.test(url);
// eslint-disable-next-line no-control-regex
const cameraText = (v, max = CAMERA_TEXT_MAX) => String(v == null ? '' : v).replace(/[\x00-\x1f\x7f]/g, ' ').trim().slice(0, max).trim();

/** The lowest unused camera number for a new section (null when 1 through MAX_CAM are occupied). */
// `baseline` = the loaded/saved config. A number present THERE is also skipped, so removing camN and then
// discovering in the same session never re-creates camN — that would push as an in-place edit of the old
// camera (and cloudapi refuses it for a local admin, #86bc63u9g).
export const nextCameraNumber = (config, family, baseline) => {
  const fam = family || detectFamily(config);
  const cams = new Set([...listConfigCameras(config, fam), ...(baseline ? listConfigCameras(baseline, fam) : [])]);
  for (let cam = 1; cam <= MAX_CAMERA_NUMBER; cam += 1) {
    if (!cams.has(cam)) return cam;
  }
  return null;
};

/**
 * Choose default main/sub RTSP URLs from a discovered camera's stream list:
 * main = highest pixel count (best quality for recording), sub = lowest (cheap
 * live preview), each falling back to the other when only one stream exists.
 * @param {Object} discovered - a discover camera ({ rtsp: [{ url, width, height, codec }] })
 * @returns {{recUrl:string, liveUrl:string, options:Array}}
 */
export const pickStreamUrls = (discovered) => {
  const streams = Array.isArray(discovered && discovered.rtsp)
    ? discovered.rtsp.filter((s) => s && s.url)
    : [];
  if (!streams.length) return { recUrl: '', liveUrl: '', options: [] };
  const px = (s) => (Number(s.width) || 0) * (Number(s.height) || 0);
  const byPx = [...streams].sort((a, b) => px(b) - px(a));
  const main = byPx[0];
  const sub = byPx[byPx.length - 1];
  return { recUrl: main.url, liveUrl: (sub && sub.url) || main.url, options: streams };
};

/**
 * Stage a discovered camera as a NEW [camN] section. IMMUTABLE (returns a new config).
 * @param {Object} config - the FULL parsed device config
 * @param {Object|null} family
 * @param {{name?:string, recUrl:string, liveUrl?:string, cam?:number}} opts
 * @returns {{config:Object, cam:?number}} the new config + assigned camera number, or null at capacity
 */
export const addDiscoveredCamera = (config, family, opts = {}) => {
  const fam = family || detectFamily(config);
  const cam = opts.cam != null ? Number(opts.cam) : nextCameraNumber(config, fam, opts.baseline);
  if (!Number.isInteger(cam) || cam < 1 || cam > MAX_CAMERA_NUMBER) return { config, cam: null };
  const key = sectionFor(fam, cam);
  const engine = engineSections(config);
  const numeric = isModernFormat(config); // modern JSON stores native number/bool; INI stays strings
  // cloudapi refuses a local admin's new camera unless its streams are rtsp(s) URLs (#86bc63u9g) — and a
  // camera with no stream never records anyway — so a discovery with no RTSP profile is NOT staged.
  const recUrl = String(opts.recUrl || '').trim();
  if (!isStreamUrl(recUrl)) return { config, cam: null, reason: 'no-stream' };
  const liveUrl = isStreamUrl(String(opts.liveUrl || '').trim()) ? String(opts.liveUrl).trim() : recUrl;
  const section = {
    [fam.urlKeys[0]]: recUrl,                         // rec_url / url1
    [fam.urlKeys[1]]: liveUrl,                        // live_url / url2
    name: cameraText(opts.name, CAMERA_TEXT_MAX * 2) || `Camera ${cam}`,
    enabled: numeric ? 1 : '1',
  };
  // Viewer metadata: the discovered hardware make/model, so the roster can show the camera TYPE next to
  // its name/IP. The device engine ignores unknown keys (the preserve-unknown invariant), so these never
  // affect it — they just ride along in the blob for the UI. Only written when discovery supplied them.
  const make = cameraText(opts.make);
  const model = cameraText(opts.model);
  if (make) section.make = make;
  if (model) section.model = model;
  return { config: rewrap(config, { ...engine, [key]: section }), cam };
};

/** Stage as many discovered cameras as the remaining camera-number range permits. */
export const addDiscoveredCameras = (config, family, selections = [], baseline) => {
  let next = config;
  const cams = [];
  let noStream = 0;
  let full = false;
  for (const selection of selections) {
    const added = addDiscoveredCamera(next, family, { ...selection, baseline });
    if (added.reason === 'no-stream') { noStream += 1; continue; }
    if (added.cam == null) { full = true; break; }
    next = added.config;
    cams.push(added.cam);
  }
  const skipped = full ? Math.max(0, selections.length - cams.length - noStream) : 0;
  return { config: next, cams, skipped, noStream };
};

/**
 * Remove a camera by dropping its whole [camN] section. IMMUTABLE (returns a NEW config); a no-op if the
 * section isn't present. The removal rides the normal "Push to Device" batch — configDirty reports it as
 * a "Camera removed" row and revertChange restores it from pristine. (#86bbm53r2 remove/replace)
 */
export const removeCamera = (config, cam, family) => {
  const fam = family || detectFamily(config);
  const key = sectionFor(fam, cam);
  const engine = engineSections(config);
  if (!(key in engine)) return config;
  const next = { ...engine };
  delete next[key];
  return rewrap(config, next);
};

/**
 * Repoint an EXISTING camera at a different stream: writes ONLY rec_url/live_url (+ make/model when given)
 * and PRESERVES every other key of the section (name, enabled, motion, LPR, …). IMMUTABLE; a no-op if the
 * camera isn't present. Used by roster "Change stream" to swap a configured camera onto a freshly
 * discovered stream WITHOUT resetting its settings (unlike addDiscoveredCamera, which builds a fresh
 * section). (#86bbm53r2 remove/replace)
 */
export const replaceCameraStream = (config, cam, opts = {}, family) => {
  const fam = family || detectFamily(config);
  const key = sectionFor(fam, cam);
  const engine = engineSections(config);
  const prev = engine[key];
  if (!prev) return config;
  const recUrl = String(opts.recUrl || '').trim();
  if (!isStreamUrl(recUrl)) return config;   // no RTSP stream — never re-point a camera at nothing
  const liveUrl = isStreamUrl(String(opts.liveUrl || '').trim()) ? String(opts.liveUrl).trim() : recUrl;
  const next = { ...prev, [fam.urlKeys[0]]: recUrl, [fam.urlKeys[1]]: liveUrl };
  const make = cameraText(opts.make);
  const model = cameraText(opts.model);
  if (make) next.make = make;
  if (model) next.model = model;
  return rewrap(config, { ...engine, [key]: next });
};

/** Deduped "make model" display (e.g. "AXIS M3007", not "AXIS AXIS M3007"); '' when neither is set. */
export const cameraType = (make, model) => {
  const mk = String(make || '').split(' ')[0];
  const md = String(model || '').trim();
  if (!md) return mk || '';
  if (!mk || md.toLowerCase().indexOf(mk.toLowerCase()) === 0) return md;
  return `${mk} ${md}`;
};

/** The camera's LAN host parsed from its RTSP URL (strips creds + port), '' if none. */
export const cameraIpFromSection = (section) => {
  const url = String((section && (section.rec_url || section.url1 || section.live_url || section.url2)) || '');
  // rtsp://[user:pass@]host[:port]/... — the host, WHETHER OR NOT credentials are embedded. The host
  // alternative captures a bracketed IPv6 literal (`[::1]`) as a unit BEFORE the bare-host form (a plain
  // `[^:]+` would stop at the first `:` and return just "["); the brackets are then stripped so this
  // normalizes to the same bare host that hostOf() derives from a discovered ip (so de-dup matches).
  const m = url.match(/:\/\/(?:[^/@]*@)?(\[[^\]]+\]|[^/:?@\s]+)/);
  const host = m ? m[1] : '';
  return host[0] === '[' ? host.slice(1, -1) : host;
};

/**
 * A device-level "form view" — every device field the family supports, seeded from engine defaults.
 * @returns {{family:string, present:Object, has:Object, [form:string]:any}}
 */
export const readDevice = (config, family) => {
  const engine = engineSections(config);
  const fam = family || detectFamily(config);
  const view = { family: fam.key, present: {}, has: {} };
  for (const d of deviceFields(fam)) {
    const sec = engine[d.section] || {};
    const hasKey = d.key in sec;
    view[d.form] = coerce(d.kind, hasKey ? sec[d.key] : d.def);
    view.present[d.form] = true;
    view.has[d.form] = hasKey;
  }
  return view;
};

/**
 * Merge a device form back into the config's sections. IMMUTABLE. Writes ONLY the writable device keys
 * the ORIGINAL config section actually carried (present-only — mirror exactly what the device sent;
 * David 2026-08-26), preserving all other sections/keys. A field the device omitted is never added.
 * @param {Object} config
 * @param {Object|null} family
 * @param {Object} form - a readDevice()-shaped object
 * @returns {Object} the new full config
 */
export const writeDevice = (config, family, form) => {
  const engine = engineSections(config);
  const fam = family || detectFamily(config);
  const numeric = isModernFormat(config);   // modern JSON stores native number/bool; INI stays strings
  const next = { ...engine };
  for (const d of deviceFields(fam)) {
    // NO access check here. Editability is decided ONLY by the single source (setupAccess) at render time;
    // write persists any present key whose VALUE CHANGED, and reconcileTypes() echoes it back to the
    // device's own JSON type on save. Writing only what changed means an untouched field (read-only or
    // simply not edited) is a no-op that leaves its section's reference intact — so the schema `access` no
    // longer gates persistence, yet nothing is needlessly rewritten. (David 2026-08-27)
    if (form[d.form] === undefined) continue;
    const prevSec = engine[d.section] || {};
    if (!Object.prototype.hasOwnProperty.call(prevSec, d.key)) continue;   // device didn't send this key — don't add it
    const serialized = serialize(d.kind, form[d.form], numeric);
    if (serialized === prevSec[d.key]) continue;   // unchanged — don't rewrite (preserves ref; RO fields no-op)
    next[d.section] = { ...(next[d.section] || {}) };
    next[d.section][d.key] = serialized;
  }
  return rewrap(config, next);
};

/**
 * TYPE-ECHO the save payload back to the DEVICE'S OWN JSON types (#86bbm58f7 — the device-safety pass).
 *
 * The device is the source of truth for every field's type. This walks the outgoing config and coerces
 * each leaf to the JSON type it has in `base` (the config AS LOADED from the device via the DB) at the same
 * path — a field the device sent as a number goes back a NUMBER, a string stays a STRING, a boolean stays a
 * BOOLEAN. So a present field can NEVER be mis-typed (we just echo what the device gave us), and when the
 * device switches booleans from `0/1` to real `true`/`false`, we follow automatically with ZERO code change
 * (its `base` type flips → so does our echo). No hard-coded per-field type can be wrong here.
 *
 * Safety invariants:
 *   - Only leaves that EXIST in `base` are touched. A key the device didn't send is left exactly as-is
 *     (we never invent a type for it — and per product rule we only surface/edit present fields anyway).
 *   - A numeric coercion that would produce NaN/non-finite is REFUSED — the original value is kept, so a
 *     bad edit degrades to "unchanged", never to corrupt JSON that could brick the device.
 *   - Immutable: returns a new deep-cloned config; `next` and `base` are not mutated.
 *
 * @param {Object} next - the assembled save payload (writeDevice output + connector edits)
 * @param {Object} base - the config as loaded from the device/DB (native types)
 * @returns {Object} a new config with every present leaf coerced to the device's own type
 */
export const reconcileTypes = (next, base) => {
  const walk = (n, b) => {
    if (n == null || typeof n !== 'object' || b == null || typeof b !== 'object') return;
    for (const k of Object.keys(n)) {
      if (!Object.prototype.hasOwnProperty.call(b, k)) continue;   // device didn't send this key — leave it
      const nv = n[k];
      const bv = b[k];
      if (nv && typeof nv === 'object' && bv && typeof bv === 'object') { walk(nv, bv); continue; }
      if (typeof bv === 'number' && typeof nv !== 'number') {
        const num = Number(nv);
        // string→number when it's a finite number; otherwise (empty, NaN, "5px", cleared field) fall back to
        // the DEVICE'S OWN value — never write a string/empty to a field the device sent as a number. This is
        // what makes "a present field can never be mis-typed" actually TRUE for garbage input. (#86bbm58f7)
        n[k] = (nv !== '' && nv != null && Number.isFinite(num)) ? num : bv;
      } else if (typeof bv === 'string' && typeof nv !== 'string') {
        n[k] = nv == null ? '' : String(nv);                               // number/bool→string
      } else if (typeof bv === 'boolean' && typeof nv !== 'boolean') {
        n[k] = boolFromCfg(nv);                                            // device flipped this to a JSON bool — follow
      }
    }
  };
  const clone = JSON.parse(JSON.stringify(next));
  walk(clone, base);
  return clone;
};

/**
 * The keys in a camera's section that our family schema does NOT manage — so the UI can surface them
 * READ-ONLY ("nothing the engine knows about is hidden"). Returns [{key, value}] (value stringified).
 */
export const otherCameraKeys = (config, cam, family) => {
  const fam = family || detectFamily(config);
  const s = cameraSection(config, cam, fam);
  const managed = new Set(cameraFields(fam).map((d) => d.key));
  return Object.keys(s)
    .filter((k) => !managed.has(k))
    .map((k) => ({ key: k, value: s[k] == null ? '' : String(s[k]) }));
};

/**
 * The device-level section keys our family schema does NOT manage (excluding TLS material) — for a
 * read-only "Additional settings" group. Returns [{section, key, value}], grouped-section order.
 */
export const otherDeviceKeys = (config, family) => {
  const engine = engineSections(config);
  const fam = family || detectFamily(config);
  const managed = new Set(deviceFields(fam).map((d) => `${d.section}.${d.key}`));
  const out = [];
  for (const section of Object.keys(engine)) {
    if (/^cam(era)?\d+$/i.test(section)) continue;          // camera sections belong to the Cameras screen
    const sec = engine[section];
    if (!sec || typeof sec !== 'object') continue;
    for (const key of Object.keys(sec)) {
      if (DEVICE_SKIP[section] && DEVICE_SKIP[section][key]) continue;   // TLS material
      if (managed.has(`${section}.${key}`)) continue;
      // `raw` preserves the native JSON value so the UI can infer the input type (number vs string vs
      // bool); `value` is the display string. Editing routes through setEngineKey + reconcileTypes.
      out.push({ section, key, value: sec[key] == null ? '' : String(sec[key]), raw: sec[key] });
    }
  }
  return out;
};

/**
 * Immutably set ONE engine-section key to `value`, preserving every other section/key. This is how an
 * UNMANAGED ("Other"/"Misc") config key is edited straight in the blob: writeDevice() preserves it and
 * reconcileTypes() echoes it back to the device's own JSON type on save, so a number stays a number and
 * a string stays a string with zero per-key schema. (David 2026-08-27 — every config item is editable.)
 */
export const setEngineKey = (config, section, key, value) => {
  const engine = engineSections(config);
  const next = { ...engine, [section]: { ...(engine[section] || {}), [key]: value } };
  return rewrap(config, next);
};

// ---------------------------------------------------------------------------
// Connector (POS) — modern JSON only. read* helpers below; writable fields are edited via
// CloudDeviceSetup's patchConn (UI editable for master; DB write still gated — console.log only).
// ---------------------------------------------------------------------------

/**
 * A connector-level "form view" — every connector field, seeded from defaults.
 * Returns null if the config has no connector namespace (INI/legacy configs).
 */
export const readConnector = (config) => {
  const conn = connectorSections(config);
  if (!conn) return null;
  const view = { present: {}, has: {} };
  for (const d of ALL_CONNECTOR_FIELDS) {
    const sec = conn[d.section] || {};
    const hasKey = d.key in sec;
    view[d.form] = coerce(d.kind, hasKey ? sec[d.key] : d.def);
    view.present[d.form] = true;
    view.has[d.form] = hasKey;
  }
  return view;
};

/**
 * The connector-level section keys our schema does NOT manage — for a read-only "Additional" dump.
 * Returns [{section, key, value}] or [] if no connector namespace.
 */
export const otherConnectorKeys = (config) => {
  const conn = connectorSections(config);
  if (!conn) return [];
  const managed = new Set(ALL_CONNECTOR_FIELDS.map((d) => `${d.section}.${d.key}`));
  const out = [];
  for (const section of Object.keys(conn)) {
    if (/^pos\d+$/i.test(section)) continue;   // POS sources are variable-count; handled separately
    const sec = conn[section];
    if (!sec || typeof sec !== 'object') continue;
    for (const key of Object.keys(sec)) {
      if (managed.has(`${section}.${key}`)) continue;
      out.push({ section, key, value: sec[key] == null ? '' : String(sec[key]), raw: sec[key] });
    }
  }
  return out;
};

/** Immutably set ONE connector-section key (modern JSON only; returns config unchanged for INI). */
export const setConnectorKey = (config, section, key, value) => {
  if (!isModernFormat(config)) return config;
  const conn = config.files.connector || {};
  return { format: config.format, files: { ...config.files, connector: { ...conn, [section]: { ...(conn[section] || {}), [key]: value } } } };
};

/**
 * List the POS source numbers that HAVE a section in the connector config (posN), sorted.
 * Returns [] if no connector namespace.
 */
export const listConnectorPos = (config) => {
  const conn = connectorSections(config);
  if (!conn) return [];
  return Object.keys(conn)
    .map((k) => { const m = k.match(/^pos(\d+)$/i); return m ? Number(m[1]) : null; })
    .filter((n) => n != null)
    .sort((a, b) => a - b);
};

/**
 * The raw section object for one POS source (or {}).
 */
export const posSection = (config, num) => {
  const conn = connectorSections(config);
  return (conn && conn['pos' + num]) || {};
};

// The connector (POS) shell the cloud UI authors when POS is first ENABLED on a device (86bc1nuf9).
// A dragon device with no files.connector shows no POS tab (the tab is content-gated and the only
// "add lane" control lives inside it), and the device won't self-seed once cloudapi holds any config —
// so the cloud must write the outer shell (global/rpc/dragon/cloud, no posN yet) before the first lane
// can be added. Values mirror a healthy device (BAF779EDCA86 + connector.conf.example) and are STRINGS
// (files.connector values always are). DELIBERATELY OMITTED — the keys the cloudapi authorization gate
// owns or assigns and would 403 from a client: global.conf_rev (server compare-and-increments it on
// write), global.serial (read-only device fact), and cloud.api/ws/api_token (backend-assigned,
// read-only). The server fills conf_rev; the rest arrive when the device/provisioning uploads them.
const CONNECTOR_SHELL = Object.freeze({
  global: { jpeg_quality: '70', tmpdir: '/tmp/rda', ticketsdir: '/videostore/vs1/dividia/tickets', dbfile: '/var/lib/connector/connector.db', logdir: '/var/log', pair_tare_loadout: 'off' },
  rpc: { bind: '127.0.0.1', port: '43204' },
  dragon: { host: '127.0.0.1', port: '80', user: '', pass: '', snapshot_profile: '1', meta_profile: '1', conf: '/usr/local/etc/dragon.conf' },
  cloud: { enabled: 'false' },
});

/**
 * Is the connector (POS) block present on this device? True only for a modern device that already has a
 * non-empty files.connector. False for legacy/INI and for a modern device that has never had POS. The
 * POS tab's Enable/Disable toggle reads this to decide on vs off. (86bc1nuf9)
 */
export const hasConnectorBlock = (config) => {
  const conn = connectorSections(config);
  return !!conn && Object.keys(conn).length > 0;
};

/**
 * Seed the connector shell so the cloud UI can author the first POS lane — "Enable POS" (86bc1nuf9).
 * No-op if the device isn't modern or a block already exists (never clobbers a real config). Immutable:
 * returns a new config; the shell is deep-copied so callers can mutate it freely.
 */
export const enableConnector = (config) => {
  if (!isModernFormat(config) || hasConnectorBlock(config)) return config;
  return { format: config.format, files: { ...config.files, connector: JSON.parse(JSON.stringify(CONNECTOR_SHELL)) } };
};

/**
 * Remove the ENTIRE connector (POS) block — "Disable POS" (86bc1nuf9). Immutable; returns config
 * unchanged for legacy/INI (no connector namespace). cloudapi persists the removal by full-config
 * replace, but a master must clear its read-only leaves (conf_rev/serial/cloud.*) with it — the
 * server gate exempts a whole-connector-file deletion for exactly this.
 */
export const disableConnector = (config) => {
  if (!isModernFormat(config)) return config;
  const files = { ...config.files };
  delete files.connector;
  return { format: config.format, files };
};
